import React, { useEffect, useState } from 'react'
import axiosInstance from '../Api/axiosInstance'
import Navbar from '../Component/Navbar'
import { useNavigate } from 'react-router-dom'
import {
    AreaChart, Area, BarChart, Bar,
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts'

const API_BASE = 'https://api.imtransporter.com'

const OL       = '#6b7c3e'
const OL_LIGHT = '#8a9e52'
const OL_PALE  = '#f0f4e8'
const OL_DARK  = '#3d4a22'

const navItems = [
    { label: 'Dashboard',        icon: '📊', path: '/dashboard' },
    { label: 'Trip Details',     icon: '🚚', path: '/trip-details' },
    { label: 'Saved Address',    icon: '📍', path: '/save-address' },
    { label: 'Employee Details', icon: '👤', path: '/employee-request' },
    { label: 'Branch Details',   icon: '🏢', path: '/branch-details' },
    { label: 'Account',          icon: '📋', path: '/account' },
]

const statusMap = {
    1: { label: 'Pending',     color: '#b45309', bg: '#fef3c7' },
    2: { label: 'Assigned',    color: '#0369a1', bg: '#e0f2fe' },
    3: { label: 'Completed',   color: '#15803d', bg: '#dcfce7' },
    4: { label: 'Cancelled',   color: '#b91c1c', bg: '#fee2e2' },
    5: { label: 'In Progress', color: '#6d28d9', bg: '#ede9fe' },
}

const PERIOD_OPTIONS = [
    { label: 'Last 7 Days',  key: 'last7'  },
    { label: 'Last 30 Days', key: 'last30' },
    { label: 'Last 60 Days', key: 'last60' },
    { label: 'Last 90 Days', key: 'last90' },
]

// ── Chart helpers ─────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
}

/** Merge tripFrequency + distanceTrend by date */
const buildTripDistanceData = (tripFrequency = [], distanceTrend = []) => {
    const map = {}
    tripFrequency.forEach(t => {
        map[t._id] = { date: formatDate(t._id), rawDate: t._id, trips: t.count, distance: 0 }
    })
    distanceTrend.forEach(d => {
        if (!map[d._id]) map[d._id] = { date: formatDate(d._id), rawDate: d._id, trips: 0, distance: 0 }
        map[d._id].distance = parseFloat(d.distance.toFixed(2))
    })
    return Object.values(map).sort((a, b) => a.rawDate.localeCompare(b.rawDate))
}

/** Pivot paymentTrend into { date, cash, online } rows */
const buildPaymentData = (paymentTrend = []) => {
    const map = {}
    paymentTrend.forEach(p => {
        const date = p._id.date
        const mode = p._id.mode
        if (!map[date]) map[date] = { date: formatDate(date), rawDate: date, cash: 0, online: 0 }
        map[date][mode] = (map[date][mode] || 0) + p.amount
    })
    return Object.values(map).sort((a, b) => a.rawDate.localeCompare(b.rawDate))
}

// ── Custom Tooltip ─────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, prefix = '' }) => {
    if (!active || !payload?.length) return null
    return (
        <div style={{
            background: '#fff', border: '1px solid #e2e8d4', borderRadius: 10,
            padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            fontSize: 13, fontFamily: "'DM Sans', sans-serif",
        }}>
            <p style={{ fontWeight: 700, color: OL_DARK, marginBottom: 6 }}>{label}</p>
            {payload.map(p => (
                <p key={p.dataKey} style={{ color: p.color, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600 }}>{p.name}:</span>{' '}
                    {prefix}{typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}
                </p>
            ))}
        </div>
    )
}

// ── Dashboard ──────────────────────────────────────────────────────────────────

const Dashboard = () => {
    const navigate = useNavigate()
    const [summary, setSummary]     = useState(null)
    const [charts, setCharts]       = useState(null)
    const [loading, setLoading]     = useState(true)
    const [error, setError]         = useState('')
    const [period, setPeriod]       = useState('last60')
    const [activeNav, setActiveNav] = useState('Dashboard')

    useEffect(() => {
        const fetchDashboard = async () => {
            setLoading(true)
            setError('')
            try {
                const [summaryRes, chartsRes] = await Promise.all([
                    axiosInstance.get(`/enterprise/admin/analytics/dashboard/summary`, { useAuth: true }),
                    axiosInstance.get(`/enterprise/admin/analytics/dashboard/charts`,  { useAuth: true }),
                ])
                setSummary(summaryRes.data?.data || summaryRes.data)
                setCharts(chartsRes.data?.data  || chartsRes.data)
            } catch (err) {
                setError(err?.response?.data?.message || err.message || 'Failed to load dashboard.')
            } finally {
                setLoading(false)
            }
        }
        fetchDashboard()
    }, [])

    if (loading) return (
        <>
            <Navbar />
            <div style={S.centered}>
                <div style={S.spinner} />
                <p style={{ color: '#888', marginTop: 14, fontSize: 14 }}>Loading dashboard...</p>
            </div>
        </>
    )

    if (error) return (
        <>
            <Navbar />
            <div style={S.centered}>
                <div style={S.errorBox}>⚠️ {error}</div>
            </div>
        </>
    )

    const trips    = summary?.tripsCount
    const distance = summary?.totalDistance
    const payment  = summary?.paymentCollection
    const recent   = summary?.recentTrips || []

    // ── Chart data ──
    const tripDistanceData = buildTripDistanceData(charts?.tripFrequency, charts?.distanceTrend)
    const paymentData      = buildPaymentData(charts?.paymentTrend)
    const hasChartData     = tripDistanceData.length > 0 || paymentData.length > 0

    const statCards = [
        {
            icon: '🚚', label: 'Total Trips',
            value: trips?.[period] ?? 0,
            sub: 'trips in period',
            color: OL, bg: OL_PALE,
        },
        {
            icon: '📍', label: 'Total Distance',
            value: `${distance?.[period] ?? 0} km`,
            sub: 'distance covered',
            color: '#0369a1', bg: '#e0f2fe',
        },
        {
            icon: '💵', label: 'Cash Collected',
            value: `₹${payment?.cash?.[period] ?? 0}`,
            sub: 'cash payments',
            color: '#15803d', bg: '#dcfce7',
        },
        {
            icon: '💳', label: 'Online Collected',
            value: `₹${payment?.online?.[period] ?? 0}`,
            sub: 'online payments',
            color: '#6d28d9', bg: '#ede9fe',
        },
        {
            icon: '🏦', label: 'Total Collection',
            value: `₹${payment?.total?.[period] ?? 0}`,
            sub: 'all payments',
            color: '#b45309', bg: '#fef3c7',
        },
    ]

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: 'DM Sans', sans-serif; }
                .nav-btn:hover { background: ${OL_PALE} !important; color: ${OL_DARK} !important; }
                .period-btn:hover { background: ${OL_PALE} !important; }
                .recent-row:hover td { background: #f4f7f0 !important; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .stat-card { animation: fadeUp 0.4s ease both; }
                .recharts-tooltip-cursor { fill: ${OL_PALE} !important; }
            `}</style>

            <Navbar />
            <div style={S.shell}>

                {/* ── Sidebar ── */}
                <aside style={S.sidebar}>
                    <div style={S.sidebarLogo}>
                        <span style={S.logoIcon}>🚛</span>
                        <span style={S.logoText}>Menu</span>
                    </div>
                    <nav style={S.nav}>
                        {navItems.map(item => (
                            <button
                                key={item.label}
                                className="nav-btn"
                                style={{
                                    ...S.navItem,
                                    ...(activeNav === item.label ? S.navItemActive : {})
                                }}
                                onClick={() => {
                                    setActiveNav(item.label)
                                    navigate(item.path)
                                }}
                            >
                                <span style={S.navIcon}>{item.icon}</span>
                                <span>{item.label}</span>
                                {activeNav === item.label && <span style={S.navIndicator} />}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* ── Main ── */}
                <main style={S.main}>

                    {/* Page Header */}
                    <div style={S.pageHeader}>
                        <div>
                            <h1 style={S.title}>Dashboard</h1>
                            <p style={S.subtitle}>Overview of your enterprise analytics</p>
                        </div>
                        <div style={S.periodRow}>
                            {PERIOD_OPTIONS.map(opt => (
                                <button
                                    key={opt.key}
                                    className="period-btn"
                                    style={{
                                        ...S.periodBtn,
                                        ...(period === opt.key ? S.periodBtnActive : {})
                                    }}
                                    onClick={() => setPeriod(opt.key)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── Stat Cards ── */}
                    <div style={S.statsGrid}>
                        {statCards.map((card, i) => (
                            <div
                                key={card.label}
                                className="stat-card"
                                style={{ ...S.statCard, animationDelay: `${i * 70}ms` }}
                            >
                                <div style={{ ...S.statIconWrap, background: card.bg }}>
                                    <span style={S.statIcon}>{card.icon}</span>
                                </div>
                                <div style={S.statBody}>
                                    <p style={S.statLabel}>{card.label}</p>
                                    <p style={{ ...S.statValue, color: card.color }}>{card.value}</p>
                                    <p style={S.statSub}>{card.sub}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Payment Breakdown ── */}
                    <div style={S.card}>
                        <h2 style={S.cardTitle}>💰 Payment Breakdown — {PERIOD_OPTIONS.find(o => o.key === period)?.label}</h2>
                        <div style={S.paymentGrid}>
                            {[
                                { label: 'Cash',   value: payment?.cash?.[period]   ?? 0, color: '#15803d', bg: '#dcfce7', icon: '💵' },
                                { label: 'Online', value: payment?.online?.[period] ?? 0, color: '#0369a1', bg: '#e0f2fe', icon: '💳' },
                                { label: 'Total',  value: payment?.total?.[period]  ?? 0, color: OL_DARK,   bg: OL_PALE,   icon: '🏦' },
                            ].map(p => {
                                const total = payment?.total?.[period] || 1
                                const pct   = Math.round((p.value / total) * 100)
                                return (
                                    <div key={p.label} style={S.paymentCard}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                            <span style={{ ...S.payIcon, background: p.bg }}>{p.icon}</span>
                                            <span style={S.payLabel}>{p.label}</span>
                                        </div>
                                        <p style={{ ...S.payValue, color: p.color }}>₹{p.value}</p>
                                        <div style={S.progressBg}>
                                            <div style={{
                                                ...S.progressFill,
                                                width: `${p.label === 'Total' ? 100 : pct}%`,
                                                background: p.color,
                                            }} />
                                        </div>
                                        <p style={S.payPct}>{p.label === 'Total' ? '100%' : `${pct}% of total`}</p>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* ── Period Comparison Table ── */}
                    <div style={S.card}>
                        <h2 style={S.cardTitle}>📈 Period Comparison</h2>
                        <div style={S.tableWrap}>
                            <table style={S.table}>
                                <thead>
                                    <tr>
                                        {['Metric', 'Last 7 Days', 'Last 30 Days', 'Last 60 Days', 'Last 90 Days'].map(h => (
                                            <th key={h} style={S.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { label: '🚚 Trips',           data: trips,            fmt: v => v },
                                        { label: '📍 Distance (km)',   data: distance,         fmt: v => v },
                                        { label: '💵 Cash (₹)',        data: payment?.cash,    fmt: v => `₹${v}` },
                                        { label: '💳 Online (₹)',      data: payment?.online,  fmt: v => `₹${v}` },
                                        { label: '🏦 Total (₹)',       data: payment?.total,   fmt: v => `₹${v}` },
                                    ].map(row => (
                                        <tr key={row.label} className="recent-row" style={S.tr}>
                                            <td style={{ ...S.td, fontWeight: 600, color: OL_DARK }}>{row.label}</td>
                                            {['last7', 'last30', 'last60', 'last90'].map(k => (
                                                <td key={k} style={{
                                                    ...S.td,
                                                    fontWeight: k === period ? 700 : 400,
                                                    color: k === period ? OL : '#3a3a3a',
                                                    background: k === period ? OL_PALE : '#fff',
                                                }}>
                                                    {row.fmt(row.data?.[k] ?? 0)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Charts Section ── */}
                    {hasChartData ? (
                        <>
                            {/* Trip Frequency Bar Chart */}
                            {charts?.tripFrequency?.length > 0 && (
                                <div style={S.card}>
                                    <h2 style={S.cardTitle}>🚚 Trip Frequency</h2>
                                    <p style={S.chartSubtitle}>Number of trips per day</p>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={buildTripDistanceData(charts.tripFrequency, [])} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e8eedd" vertical={false} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#888', fontFamily: "'DM Sans'" }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fontSize: 12, fill: '#888', fontFamily: "'DM Sans'" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Bar dataKey="trips" name="Trips" fill={OL} radius={[6, 6, 0, 0]} maxBarSize={48} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Distance Trend Area Chart */}
                            {charts?.distanceTrend?.length > 0 && (
                                <div style={S.card}>
                                    <h2 style={S.cardTitle}>📍 Distance Trend</h2>
                                    <p style={S.chartSubtitle}>Total km covered per day</p>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <AreaChart data={buildTripDistanceData([], charts.distanceTrend)} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="distGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%"  stopColor="#0369a1" stopOpacity={0.18} />
                                                    <stop offset="95%" stopColor="#0369a1" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e8eedd" vertical={false} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#888', fontFamily: "'DM Sans'" }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fontSize: 12, fill: '#888', fontFamily: "'DM Sans'" }} axisLine={false} tickLine={false} unit=" km" />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area
                                                type="monotone" dataKey="distance" name="Distance (km)"
                                                stroke="#0369a1" strokeWidth={2.5}
                                                fill="url(#distGrad)" dot={{ fill: '#0369a1', r: 4, strokeWidth: 0 }}
                                                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Payment Trend Composed Chart */}
                            {charts?.paymentTrend?.length > 0 && (
                                <div style={S.card}>
                                    <h2 style={S.cardTitle}>💰 Payment Trend</h2>
                                    <p style={S.chartSubtitle}>Cash vs Online payments per day (₹)</p>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <ComposedChart data={paymentData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%"  stopColor="#15803d" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#15803d" stopOpacity={0} />
                                                </linearGradient>
                                                <linearGradient id="onlineGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%"  stopColor="#6d28d9" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#6d28d9" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e8eedd" vertical={false} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#888', fontFamily: "'DM Sans'" }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fontSize: 12, fill: '#888', fontFamily: "'DM Sans'" }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v}`} />
                                            <Tooltip content={<CustomTooltip prefix="₹" />} />
                                            <Legend
                                                iconType="circle" iconSize={9}
                                                wrapperStyle={{ fontSize: 13, fontFamily: "'DM Sans'", paddingTop: 12 }}
                                            />
                                            <Bar dataKey="cash" name="Cash" fill="#15803d" radius={[4, 4, 0, 0]} maxBarSize={36} opacity={0.85} />
                                            <Bar dataKey="online" name="Online" fill="#6d28d9" radius={[4, 4, 0, 0]} maxBarSize={36} opacity={0.85} />
                                            <Line
                                                type="monotone" dataKey="cash" name="Cash Trend"
                                                stroke="#15803d" strokeWidth={2} dot={false}
                                                strokeDasharray="4 3" legendType="none"
                                            />
                                            <Line
                                                type="monotone" dataKey="online" name="Online Trend"
                                                stroke="#6d28d9" strokeWidth={2} dot={false}
                                                strokeDasharray="4 3" legendType="none"
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={S.card}>
                            <h2 style={S.cardTitle}>📊 Charts</h2>
                            <div style={S.chartsPlaceholder}>
                                <span style={{ fontSize: 36 }}>📉</span>
                                <p style={{ color: '#888', marginTop: 8 }}>No chart data available yet</p>
                            </div>
                        </div>
                    )}

                    {/* ── Recent Trips ── */}
                    <div style={S.card}>
                        <h2 style={S.cardTitle}>🕐 Recent Trips</h2>
                        {recent.length === 0 ? (
                            <div style={S.empty}>
                                <span style={{ fontSize: 32 }}>🚚</span>
                                <p>No recent trips found</p>
                            </div>
                        ) : (
                            <div style={S.tableWrap}>
                                <table style={S.table}>
                                    <thead>
                                        <tr>
                                            {['Trip Code', 'Distance (km)', 'Status', 'Driver', 'Created At'].map(h => (
                                                <th key={h} style={S.th}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recent.map(trip => {
                                            const st = statusMap[trip.status] || statusMap[1]
                                            return (
                                                <tr key={trip._id} className="recent-row" style={S.tr}>
                                                    <td style={S.td}>
                                                        <span style={S.tripCode}>{trip.tripCode}</span>
                                                    </td>
                                                    <td style={{ ...S.td, fontWeight: 600, color: OL_DARK }}>
                                                        {trip.totalDistance} km
                                                    </td>
                                                    <td style={S.td}>
                                                        <span style={{
                                                            ...S.statusBadge,
                                                            color: st.color,
                                                            background: st.bg,
                                                            border: `1px solid ${st.color}40`,
                                                        }}>
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                    <td style={S.td}>
                                                        {trip.driverId
                                                            ? <span style={S.driverBadge}>✅ Assigned</span>
                                                            : <span style={S.noDriverBadge}>⏳ Unassigned</span>
                                                        }
                                                    </td>
                                                    <td style={{ ...S.td, color: '#888', fontSize: 12 }}>
                                                        {new Date(trip.createdAt).toLocaleString()}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                </main>
            </div>
        </>
    )
}

export default Dashboard

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
    shell: {
        display: 'flex', minHeight: '100vh',
        fontFamily: "'DM Sans', sans-serif",
        background: '#f7f8f4', color: '#2c2c2c',
    },
    sidebar: {
        width: 220, background: '#fff',
        borderRight: '1px solid #e0e5d4',
        display: 'flex', flexDirection: 'column',
        padding: '24px 0', flexShrink: 0,
        boxShadow: '2px 0 12px rgba(107,124,62,0.07)',
    },
    sidebarLogo: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 20px 24px', borderBottom: '1px solid #e8eedd', marginBottom: 12,
    },
    logoIcon: { fontSize: 22 },
    logoText: { fontSize: 18, fontWeight: 700, color: OL_DARK, letterSpacing: '-0.3px' },
    nav: { display: 'flex', flexDirection: 'column', gap: 2, padding: '0 10px' },
    navItem: {
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
        borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer',
        fontSize: 14, fontWeight: 500, color: '#666', textAlign: 'left',
        position: 'relative', transition: 'all 0.18s ease', fontFamily: "'DM Sans', sans-serif",
    },
    navItemActive: { background: OL_PALE, color: OL_DARK, fontWeight: 600 },
    navIcon: { fontSize: 15 },
    navIndicator: {
        position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
        width: 3, height: 20, borderRadius: '0 3px 3px 0', background: OL,
    },
    main: { flex: 1, padding: '32px 36px', overflow: 'auto' },
    pageHeader: {
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16,
    },
    title: { fontSize: 26, fontWeight: 700, color: OL_DARK, letterSpacing: '-0.5px' },
    subtitle: { fontSize: 13, color: '#888', marginTop: 3 },
    periodRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    periodBtn: {
        padding: '7px 14px', borderRadius: 20,
        border: '1.5px solid #dde5cc', background: '#fff',
        color: '#555', fontWeight: 500, fontSize: 13,
        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
        transition: 'all 0.15s ease',
    },
    periodBtnActive: {
        background: OL, color: '#fff',
        border: `1.5px solid ${OL}`, fontWeight: 700,
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16, marginBottom: 24,
    },
    statCard: {
        background: '#fff', borderRadius: 14,
        border: '1px solid #e2e8d4',
        padding: '20px 18px',
        boxShadow: '0 2px 12px rgba(107,124,62,0.07)',
        display: 'flex', alignItems: 'flex-start', gap: 14,
    },
    statIconWrap: {
        width: 46, height: 46, borderRadius: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
    },
    statIcon: { fontSize: 22 },
    statBody: { flex: 1 },
    statLabel: { fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 4 },
    statValue: { fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px' },
    statSub: { fontSize: 11, color: '#aaa', marginTop: 3 },
    card: {
        background: '#fff', borderRadius: 14,
        border: '1px solid #e2e8d4',
        padding: '22px 24px', marginBottom: 22,
        boxShadow: '0 2px 12px rgba(107,124,62,0.07)',
    },
    cardTitle: {
        fontSize: 15, fontWeight: 700, color: OL_DARK,
        marginBottom: 4, paddingBottom: 12,
        borderBottom: '1px solid #e8eedd',
    },
    chartSubtitle: {
        fontSize: 12, color: '#aaa', marginBottom: 18, marginTop: 4,
    },
    paymentGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16,
    },
    paymentCard: {
        background: '#fafafa', borderRadius: 12,
        border: '1px solid #eef1e8', padding: '16px 18px',
    },
    payIcon: {
        width: 32, height: 32, borderRadius: 8,
        display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 16,
    },
    payLabel: { fontSize: 13, fontWeight: 600, color: OL_DARK },
    payValue: { fontSize: 24, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.5px' },
    progressBg: {
        height: 6, borderRadius: 99,
        background: '#e8eedd', overflow: 'hidden', marginBottom: 6,
    },
    progressFill: { height: '100%', borderRadius: 99, transition: 'width 0.5s ease' },
    payPct: { fontSize: 11, color: '#999' },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: {
        background: '#e8eedd', color: OL_DARK, fontWeight: 600,
        padding: '11px 16px', textAlign: 'left', fontSize: 13,
        letterSpacing: '0.01em', borderBottom: `2px solid ${OL}30`,
        whiteSpace: 'nowrap',
    },
    tr: { borderBottom: '1px solid #eef1e8', transition: 'background 0.15s' },
    td: {
        padding: '12px 16px', color: '#3a3a3a',
        verticalAlign: 'middle', background: '#fff',
        transition: 'background 0.15s',
    },
    tripCode: {
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12, fontWeight: 500, color: OL_DARK,
        background: OL_PALE, padding: '3px 8px', borderRadius: 6,
    },
    statusBadge: {
        display: 'inline-block', padding: '3px 10px',
        borderRadius: 20, fontSize: 11, fontWeight: 700,
        letterSpacing: '0.03em', textTransform: 'uppercase',
    },
    driverBadge: {
        display: 'inline-block', padding: '3px 10px',
        borderRadius: 20, fontSize: 11, fontWeight: 700,
        background: '#dcfce7', color: '#15803d',
        border: '1px solid #15803d40',
    },
    noDriverBadge: {
        display: 'inline-block', padding: '3px 10px',
        borderRadius: 20, fontSize: 11, fontWeight: 700,
        background: '#fef3c7', color: '#b45309',
        border: '1px solid #b4530940',
    },
    empty: {
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 8,
        padding: '32px 0', color: '#aaa', fontSize: 14,
    },
    chartsPlaceholder: {
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', padding: '32px 0',
        color: '#aaa', textAlign: 'center',
    },
    centered: {
        minHeight: '100vh', display: 'flex',
        flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', fontFamily: "'DM Sans', sans-serif",
        background: '#f7f8f4',
    },
    spinner: {
        width: 32, height: 32,
        border: `3px solid ${OL_PALE}`,
        borderTopColor: OL, borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    errorBox: {
        padding: '12px 20px', background: '#fff1f2',
        border: '1.5px solid #fca5a5', borderRadius: 10,
        color: '#b91c1c', fontSize: 14, fontWeight: 500,
    },
}